import { useCallback } from "react";
import { MessageSquare, Users } from "@/shared/ui/icons";
import { useSetAtom } from "jotai";
import { useTranslation } from "react-i18next";

import { useIsMobile } from "@/core/hooks";
import { isStandaloneSync } from "@/core/hooks/useIsStandalone";
import { usePermissions } from "@/core/auth/usePermissions";
import { layoutAtom, mobileActiveInstanceIdAtom } from "@/core/state";
import { addPanel, listAllPanelInstances } from "@/shell/layout";
import { kioskRoleFor } from "@/shell/render/kioskRoles";

import { LanguagePickerButton } from "./LanguagePickerModal";
import { ThemePickerButton } from "./ThemePickerModal";

export function BottomBar() {
  const isMobile = useIsMobile();
  const { roleSlug, permissions } = usePermissions();

  if (isMobile) {
    // 🩸 Un rôle en KIOSQUE n'a pas le droit d'ouvrir un panneau : c'est tout
    // l'objet du kiosque, et sur desktop c'est le garde plus bas qui le tient.
    // Sur mobile, cette branche-ci passe AVANT lui — inoffensif tant qu'aucun
    // kiosque n'atteignait le mobile ; depuis le 28-08 le chatteur y arrive, et
    // « Conversations » lui aurait rouvert son travail SANS créneau attribué,
    // contre la règle de Kayla du 18-08 (le travail n'apparaît qu'avec le
    // shift). Ses onglets sont déjà dans la barre du haut : ici, seulement le
    // chrome utilitaire.
    const enKiosque = kioskRoleFor(roleSlug, permissions) !== null;
    return (
      <footer
        data-bottombar
        className="mobile-bar relative z-[300] flex shrink-0 items-stretch justify-around border-t border-fg/[0.07]"
        style={isStandaloneSync() ? { paddingBottom: 4 } : undefined}
      >
        {/* Barre du bas mobile (demande user 2026-06-30) : accès direct aux 2
            surfaces quotidiennes (Conversations, Personnel) + langue + thème.
            Plus de Home/Docs/News — peu utiles au pouce au quotidien. */}
        {!enKiosque && (
          <>
        <PanelButton
          panelId="conversations"
          labelKey="panels.registry.conversations.title"
          fallback="Conv"
          icon={<MessageSquare className="h-5 w-5" />}
        />
        <PanelButton
          panelId="personnel"
          labelKey="panels.registry.personnel.title"
          fallback="Perso"
          icon={<Users className="h-5 w-5" />}
        />
          </>
        )}
        <LanguagePickerButton />
        <ThemePickerButton />
      </footer>
    );
  }

  // Desktop : AUCUNE barre du bas, web comme app de bureau. Kayla, 08-10-2026 :
  // « tu as laissé la barre des tâches en bas. Je t'ai dit d'enlever la barre
  // des tâches. La place, les icônes, tu peux les mettre ailleurs, mais pas
  // dans la barre des tâches. » Les écrans vivent dans le rail gauche
  // (`RailDesEcrans.tsx`), la langue dans le volet du Thème, l'horloge dans la
  // barre du haut de l'app de bureau (`TitleBar`).
  return null;
}

/**
 * Ouvre (ou re-focus si déjà ouvert) un panel sur mobile. On ne crée pas de
 * doublon : si une instance de ce panelId existe déjà dans le layout, on
 * bascule simplement dessus dans le strip d'onglets mobile.
 */
function useOpenMobilePanel() {
  const setLayout = useSetAtom(layoutAtom);
  const setActive = useSetAtom(mobileActiveInstanceIdAtom);
  return useCallback(
    (panelId: string) => {
      let focusId: string | null = null;
      setLayout((prev) => {
        const existing = listAllPanelInstances(prev).find(
          (p) => p.panelId === panelId,
        );
        if (existing) {
          focusId = existing.id;
          return prev;
        }
        try {
          const res = addPanel(prev, panelId, { kind: "active-group" });
          focusId = res.instanceId;
          return res.layout;
        } catch {
          return prev;
        }
      });
      if (focusId) setActive(focusId);
    },
    [setLayout, setActive],
  );
}

function PanelButton({
  panelId,
  labelKey,
  fallback,
  icon,
}: {
  panelId: string;
  labelKey: string;
  fallback: string;
  icon: React.ReactNode;
}) {
  const { t } = useTranslation();
  const open = useOpenMobilePanel();
  const label = t(labelKey, { defaultValue: fallback });
  return (
    <button
      type="button"
      onClick={() => open(panelId)}
      aria-label={label}
      className="mobile-tab-item h-12"
    >
      {icon}
      <span className="mobile-tab-label text-[11px]">{label}</span>
    </button>
  );
}
